import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { createPortal } from "react-dom";

interface TopBarSlotContextValue {
  /** Element the slot's content is rendered into; `null` until the top bar has mounted it. */
  target: HTMLElement | null;
  /** Ref callback the top bar hands its slot element to. */
  setTarget: (el: HTMLElement | null) => void;
}

const TopBarSlotContext = createContext<TopBarSlotContextValue | undefined>(undefined);

/**
 * Lets a page put its own controls in the top bar (far right) without the top bar knowing
 * about them: the top bar mounts an empty slot, pages render into it through a portal. The
 * provider sits in MasterUI, around both.
 */
export const TopBarSlotProvider = ({ children }: { children: React.ReactNode }) => {
  const [target, setTargetState] = useState<HTMLElement | null>(null);
  const setTarget = useCallback((el: HTMLElement | null) => setTargetState(el), []);
  const value = useMemo(() => ({ target, setTarget }), [target, setTarget]);
  return <TopBarSlotContext.Provider value={value}>{children}</TopBarSlotContext.Provider>;
};

/** For the top bar: where to mount the slot element. `undefined` outside a provider. */
export const useTopBarSlotTarget = () => useContext(TopBarSlotContext)?.setTarget;

/** For pages: renders its children in the top bar's slot. Nothing rendered without a top bar. */
export const TopBarSlot = ({ children }: { children: React.ReactNode }) => {
  const ctx = useContext(TopBarSlotContext);
  if (!ctx?.target) return null;
  return createPortal(children, ctx.target);
};
